import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Menu, X, ChevronDown, Phone, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  commercialServiceLink,
  residentialServiceLinks,
  serviceAreaLinks,
} from "@/lib/site-navigation";
import tlcLogo from "@assets/TLC_Logo_1776354004749.webp";

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isAreasOpen, setIsAreasOpen] = useState(false);
  const [location] = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
    setIsAreasOpen(false);
  }, [location]);

  const closeDropdowns = () => {
    setIsServicesOpen(false);
    setIsAreasOpen(false);
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 bg-[#1a3a1a]"
      style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
    >
      {/* Top info bar */}
      <div className="border-b border-white/10">
        <div className="container mx-auto px-4 md:px-6 flex items-center justify-center lg:justify-between py-1.5">
          <a
            href="https://maps.google.com/?q=149+West+Central+Street+Franklin+MA+02038"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs text-white/60 hover:text-white/90 transition-colors"
          >
            <MapPin className="w-3 h-3 shrink-0" aria-hidden="true" />
            149 West Central Street, Franklin, MA 02038
          </a>
          <a
            href="tel:5085051539"
            aria-label="Call TLC Associates LLC at 508-505-1539"
            className="hidden lg:flex items-center gap-1.5 text-xs text-white/60 hover:text-white/90 transition-colors"
          >
            <Phone className="w-3 h-3 shrink-0" aria-hidden="true" />
            508-505-1539
          </a>
        </div>
      </div>

      {/* Main nav row */}
      <div
        className={cn(
          "py-4 transition-shadow duration-300",
          isScrolled && "shadow-lg shadow-black/20"
        )}
      >
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between">
            <Link href="/">
              <img
                src={tlcLogo}
                alt="TLC Associates LLC - Licensed & Insured"
                width={200}
                height={125}
                className="w-40 h-auto cursor-pointer"
              />
            </Link>

            <nav aria-label="Primary" className="hidden lg:block">
              <ul className="flex items-center gap-4 xl:gap-6">
                <li
                  className="group relative"
                  onKeyDown={(event) => {
                    if (event.key === "Escape") {
                      closeDropdowns();
                      document
                        .querySelector<HTMLAnchorElement>('header a[href="/"]')
                        ?.focus();
                    }
                  }}
                >
                  <button
                    className="flex items-center font-medium tracking-wide text-white transition-colors hover:text-white/80"
                    aria-expanded={isServicesOpen}
                    aria-haspopup="true"
                    aria-controls="desktop-services-menu"
                    onClick={() => {
                      setIsServicesOpen((open) => !open);
                      setIsAreasOpen(false);
                    }}
                  >
                    Services <ChevronDown className="ml-1 h-4 w-4" aria-hidden="true" />
                  </button>
                  <div
                    id="desktop-services-menu"
                    className={cn(
                      "pointer-events-none invisible absolute left-0 top-full w-64 pt-4 opacity-0 transition-all group-hover:pointer-events-auto group-hover:visible group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:visible group-focus-within:opacity-100",
                      isServicesOpen && "pointer-events-auto visible opacity-100",
                    )}
                  >
                    <ul className="overflow-hidden rounded-lg border border-gray-100 bg-white shadow-xl">
                      {residentialServiceLinks.map((service) => (
                        <li key={service.path}>
                          <Link
                            href={service.path}
                            className="block border-b border-gray-50 px-5 py-3 text-sm font-medium text-[#1a1a1a] transition-colors last:border-0 hover:bg-[#f7f3ed] hover:text-[#2a7a2a]"
                          >
                            {service.name}
                          </Link>
                          {service.children && (
                            <ul className="bg-[#f7f3ed] py-1">
                              {service.children.map((child) => (
                                <li key={child.path}>
                                  <Link
                                    href={child.path}
                                    className="block px-8 py-2 text-sm text-[#1a1a1a] hover:text-[#2a7a2a]"
                                  >
                                    {child.name}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </li>
                <li>
                  <Link
                    href={commercialServiceLink.path}
                    className="font-medium tracking-wide text-white transition-colors hover:text-white/80"
                  >
                    {commercialServiceLink.name}
                  </Link>
                </li>
                <li
                  className="group relative"
                  onKeyDown={(event) => {
                    if (event.key === "Escape") {
                      closeDropdowns();
                      document
                        .querySelector<HTMLAnchorElement>('header a[href="/"]')
                        ?.focus();
                    }
                  }}
                >
                  <button
                    className="flex items-center font-medium tracking-wide text-white transition-colors hover:text-white/80"
                    aria-expanded={isAreasOpen}
                    aria-haspopup="true"
                    aria-controls="desktop-service-areas-menu"
                    onClick={() => {
                      setIsAreasOpen((open) => !open);
                      setIsServicesOpen(false);
                    }}
                  >
                    Service Areas <ChevronDown className="ml-1 h-4 w-4" aria-hidden="true" />
                  </button>
                  <div
                    id="desktop-service-areas-menu"
                    className={cn(
                      "pointer-events-none invisible absolute left-0 top-full w-72 pt-4 opacity-0 transition-all group-hover:pointer-events-auto group-hover:visible group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:visible group-focus-within:opacity-100",
                      isAreasOpen && "pointer-events-auto visible opacity-100",
                    )}
                  >
                    <ul className="overflow-hidden rounded-lg border border-gray-100 bg-white shadow-xl">
                      {serviceAreaLinks.map((area) => (
                        <li key={area.path}>
                          <Link
                            href={area.path}
                            className="block border-b border-gray-50 px-5 py-3 text-sm font-medium text-[#1a1a1a] transition-colors last:border-0 hover:bg-[#f7f3ed] hover:text-[#2a7a2a]"
                          >
                            {area.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </li>
                <li>
                  <Link
                    href="/gallery"
                    className="font-medium tracking-wide text-white transition-colors hover:text-white/80"
                  >
                    Gallery
                  </Link>
                </li>
                <li>
                  <Link
                    href="/contact"
                    className="font-medium tracking-wide text-white transition-colors hover:text-white/80"
                  >
                    Contact
                  </Link>
                </li>
              </ul>
            </nav>

            <div className="hidden lg:flex items-center">
              <Link
                href="/contact#estimate-form"
                className="bg-[#c84060] hover:bg-[#b53553] text-white px-6 py-3 rounded-lg font-medium tracking-wide transition-all transform hover:scale-[1.02]"
              >
                Request Your Free Estimate
              </Link>
            </div>

            <button
              id="mobile-menu-trigger"
              className="lg:hidden text-white p-2"
              aria-label="Open navigation menu"
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-navigation"
              onClick={() => setIsMobileMenuOpen(true)}
            >
              <Menu className="w-8 h-8" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

        <div
          id="mobile-navigation"
          className={cn(
            "fixed inset-0 z-50 overflow-y-auto bg-[#1a3a1a] px-6 pb-6 transition-opacity lg:hidden",
            isMobileMenuOpen
              ? "visible opacity-100"
              : "pointer-events-none invisible opacity-0",
          )}
          style={{ paddingTop: `calc(env(safe-area-inset-top, 0px) + 5rem)` }}
          aria-hidden={!isMobileMenuOpen}
          inert={!isMobileMenuOpen}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              setIsMobileMenuOpen(false);
              document.getElementById("mobile-menu-trigger")?.focus();
            }
          }}
        >
          <button
            className="absolute right-6 text-white p-2"
            style={{ top: `calc(env(safe-area-inset-top, 0px) + 1.5rem)` }}
            aria-label="Close navigation menu"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <X className="w-8 h-8" aria-hidden="true" />
          </button>

          <nav aria-label="Mobile" className="flex flex-col space-y-6">
            <ul className="space-y-5">
              <li>
                <Link href="/" className="block text-xl font-medium text-white">
                  Home
                </Link>
              </li>
              <li>
                <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-white/60">
                  Services
                </p>
                <ul className="space-y-3 border-l border-white/20 pl-4">
                  {residentialServiceLinks.map((service) => (
                    <li key={service.path}>
                      <Link href={service.path} className="block text-lg text-white/80">
                        {service.name}
                      </Link>
                      {service.children && (
                        <ul className="mt-2 space-y-2 border-l border-white/20 pl-4">
                          {service.children.map((child) => (
                            <li key={child.path}>
                              <Link href={child.path} className="block text-base text-white/70">
                                {child.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              </li>
              <li>
                <Link
                  href={commercialServiceLink.path}
                  className="block text-xl font-medium text-white"
                >
                  Commercial
                </Link>
              </li>
              <li>
                <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-white/60">
                  Service Areas
                </p>
                <ul className="space-y-3 border-l border-white/20 pl-4">
                  {serviceAreaLinks.map((area) => (
                    <li key={area.path}>
                      <Link href={area.path} className="block text-base text-white/80">
                        {area.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>
              <li className="h-px w-full bg-white/10" aria-hidden="true" />
              <li>
                <Link href="/gallery" className="block text-xl font-medium text-white">
                  Gallery
                </Link>
              </li>
              <li>
                <Link href="/contact" className="block text-xl font-medium text-white">
                  Contact
                </Link>
              </li>
            </ul>

            <div className="pt-4">
              <Link
                href="/contact#estimate-form"
                className="block w-full bg-[#c84060] text-white text-center px-6 py-4 rounded-lg font-medium text-lg"
              >
                Request Your Free Estimate
              </Link>
              <a
                href="tel:5085051539"
                className="block w-full border-2 border-[#2a7a2a] text-white text-center px-6 py-4 rounded-lg font-medium text-lg mt-4"
              >
                Call 508-505-1539
              </a>
            </div>
          </nav>
        </div>
    </header>
  );
}
